import { ComponentItem } from "designSystem/types/component-item-types";
import { QRY_VALUE_CNL_LIMIT, QUERY_RESULT_LIMIT } from "csi/constants";
import ExtendedPath from "csi/core/ExtendedPath";
import ContextConstructor from "csi/def/Context";
import Spec from "csi/core/Spec";
import csiHeading from "csi/heading/_base";
import StoreValuesConstructor from "csi/store/Values";
import { C8Node, cnl, ICache, ParameterMap } from "csi/typings";
import { sortByString } from "csi/utility/arrayUtility";
import Query from "csi/xml/Query";
import _ from "lodash";
import { action, computed, makeObservable, observable, ObservableMap, runInAction } from "mobx";
import React, { useContext } from "react";
import { applyAttributeFilterToQuery, RuleOnApplyFilterFunction, RULE_FILTER_ALL_ITEMS } from "../attributeRule/RuleService";
import {
	AttributeSpecAndValues,
	CnlsAndValues,
	ComponentFilterItem,
	convertSpecToComponentItem,
	convertValueStoreToComponentFilterItems
} from "../common";

class FilterStore {
	@observable.ref private _expandedItem: ComponentItem;
	private readonly _domainType: string;
	private _filterQuery: Query;
	private readonly _postFilter: (filterValueMap: ObservableMap<Spec, CnlsAndValues>) => void;
	private readonly _postQuickFilter: (quickFilterId: string) => void;
	private readonly _quickFilterResources: string[];
	private readonly _requestParameters: ParameterMap;
	@observable private _specs: Spec[];
	@observable _quickFilterId: string = RULE_FILTER_ALL_ITEMS;

	// react to the map itself, so set deep = false
	private _deselectValuesMap = observable.map<string, any[]>(null);
	private _filterValuesMap = observable.map<Spec, CnlsAndValues>(null, { deep: false });
	private _resetFilterMap = observable.map<string, boolean>(null);

	// no need to react to the ComponentFilterItem change, just react to the map itself, so set deep = false
	private _valueStoresBySpec = observable.map<string, ComponentFilterItem[]>(null, { deep: false });

	/** the expanded item */
	get expandedItem(): ComponentItem {
		return this._expandedItem;
	}

	/** the map which contains the items which to deselect, the key would be the spec id */
	get deselectValuesPerSpec(): ObservableMap<string, any[]> {
		return this._deselectValuesMap;
	}

	/** the domain type of the filter ger applied to */
	get domainType(): string {
		return this._domainType;
	}

	/** filter values map to track */
	get filterValuesMap(): ObservableMap<Spec, CnlsAndValues> {
		return this._filterValuesMap;
	}

	/** name and value combinations for domain type  */
	@computed get filterSpecAndValuesForDomainType(): AttributeSpecAndValues[] {
		const result: AttributeSpecAndValues[] = [];
		const { filterValuesMap, specIsInMatrix } = this;

		for (const [spec, cnlsAndValues] of filterValuesMap) {
			if (specIsInMatrix(spec)) {
				continue;
			}

			const { values, cnls } = cnlsAndValues;
			const valueItems: ComponentFilterItem[] = [];

			for (let i = 0; i < values.length; i++) {
				const v = values[i];

				valueItems.push({
					id: v?.toString(),
					name: v?.toString(),
					value: v,
					cnls: [cnls[i]],
				} as ComponentFilterItem);
			}

			result.push({
				spec,
				values: valueItems,
			});
		}

		return result;
	}

	/** name and value combinations for domain type  */
	@computed get filterSpecAndValuesForNodeInMatrixPath(): AttributeSpecAndValues[] {
		const result: AttributeSpecAndValues[] = [];
		const { filterValuesMap, specIsInMatrix } = this;

		for (const [spec, cnlsAndValues] of filterValuesMap) {
			if (!specIsInMatrix(spec)) {
				continue;
			}

			const { values, cnls } = cnlsAndValues;
			const valueItems: ComponentFilterItem[] = [];

			for (let i = 0; i < values.length; i++) {
				const v = values[i];

				valueItems.push({
					id: v?.toString(),
					name: v?.toString(),
					value: v,
					cnls: [cnls[i]],
				} as ComponentFilterItem);
			}

			result.push({
				spec,
				values: valueItems,
			});
		}

		return result;
	}

	/** the items map group by node type */
	@computed get itemsMap(): Record<string, ComponentItem[]> {
		const map: Record<string, ComponentItem[]> = {};
		const { specs } = this;

		for (const spec of specs) {
			const type = spec.matrixExtendedPath?.getEndType() || spec.getSourceClass()
				.getType();
			const targetList = map[type] = map[type] || [];
			targetList.push(convertSpecToComponentItem(spec));
		}

		// sort the attribute by name per type
		Object.values(map)
			.forEach((list) => sortByString(list, "name"));

		return map;
	}

	/** 
	 * TO DO - could be multiple matrices, so need to adjust those some day
	 * matrix path for the specs, 
	 */
	@computed get matrixPath(): ExtendedPath | null {
		const { filterSpecAndValuesForNodeInMatrixPath } = this;

		if (filterSpecAndValuesForNodeInMatrixPath.length > 0) {
			return filterSpecAndValuesForNodeInMatrixPath[0].spec.getExtendedPath();
		}

		return null;
	}

	/** node type along the matrix path if any */
	@computed get nodeTypeAlongMatrixPath(): string | null {
		const { matrixPath } = this;

		return matrixPath?.getEndType();
	}

	/** resources for quick filters if configured */
	get quickFilterResources(): string[] {
		return this._quickFilterResources;
	}

	/** quick filter id */
	get quickFilterId(): string {
		return this._quickFilterId;
	}

	/** the map contains the node types, the filters of which would be reset, the key would be the node type of those filter attributes  */
	get resetFilterMap(): ObservableMap<string, boolean> {
		return this._resetFilterMap;
	}

	/** the selected spec - being expanded one */
	@computed get selectedSpec(): Spec {
		return this.specs.find((spec) => spec.getKey() === this.expandedItem?.id);
	}

	/** the specs of all items */
	get specs(): Spec[] {
		return this._specs;
	}

	/** all the spec set map */
	@computed get specsMap(): Record<string, Spec> {
		const { specs } = this;
		const map = {};

		for (const spec of specs) {
			map[spec.getKey()] = spec;
		}

		return map;
	}

	/** the value store by spec */
	get valueStoresBySpec(): ObservableMap<string, ComponentFilterItem[]> {
		return this._valueStoresBySpec;
	}

	/** apply filters to given query; if matrix path given, then only apply filters for attributes along that path */
	applyFiltersToQuery = (query: Query, matrixPath?: ExtendedPath, onApply?: RuleOnApplyFilterFunction): void => {
		const { _filterValuesMap } = this;

		for (const [spec, { values }] of _filterValuesMap) {
			applyAttributeFilterToQuery(query, spec, values, matrixPath, onApply);
		}
	};

	/** re-expanded the spec */
	reloadValuesStore = (): void => {
		const { selectedSpec } = this;

		if (selectedSpec) {
			this.loadValueStoresMap(selectedSpec);
		}
	};

	/**
	 * Add the cnls to the map for pill group to avoid making the pills out of sync when adding back the products
	 */
	@action addProductCnlIfMatchesTheFilter(products: C8Node[], cache: ICache): void {
		const { filterValuesMap } = this;
		const ctx = new ContextConstructor(null, cache);

		for (const [spec, cnlsAndValues] of filterValuesMap) {
			const { values, cnls } = cnlsAndValues;

			ctx.forEach(products, (ctx) => {
				const product = ctx.getNode();
				const url = product.$URL;
				const value = csiHeading.toFilterValue(ctx, spec.heading);

				if (values.includes(value)) {
					this._filterValuesMap.set(spec, { values, cnls: [...cnls, url] });
				}
			});
		}
	}

	/**
	 * delete the entries from the pill group when product get removed
	 */
	@action deleteProductFromFilterValuesMap = (productUrl: cnl): void => {
		const { _filterValuesMap } = this;

		for (const [spec, cnlsAndValues] of _filterValuesMap) {
			const { values, cnls } = cnlsAndValues;
			const i = cnls.indexOf(productUrl);

			//  handle the case where one cnl and one value(cnls:[Arnold], value:[C0001]), 
			if (i > -1 && cnls.length === values.length) {
				cnls.splice(i, 1);
				const removedValue = values.splice(i, 1);

				// remove this value from the left pane as well
				const specKey = spec.getKey();
				this._deselectValuesMap.set(specKey, [...(this._deselectValuesMap.get(specKey) || []), ...removedValue]);
			} else if (cnls.length !== values.length) {
				// handle the case (cnls: [centric1, centric2], values: [true])
				cnls.splice(i, 1);
			}

			if (values.length == 0) {
				_filterValuesMap.delete(spec);
			} else {
				_filterValuesMap.set(spec, { cnls, values });
			}
		}
	};

	/** load the value stores based on selected spec */
	@action async loadValueStoresMap(selectedSpec: Spec): Promise<void> {
		const { _domainType, _filterQuery, _requestParameters, specIsInMatrix, filterValuesMap } = this;
		const inMatrix = specIsInMatrix(selectedSpec);

		// go through the map of selected values per spec
		const q = new Query();
		q.importQuery(_filterQuery);

		for (const [spec, { values }] of filterValuesMap) {
			// omit the condition for the selected spec
			if (spec === selectedSpec) {
				continue;
			}

			applyAttributeFilterToQuery(q, spec, values, selectedSpec.matrixExtendedPath);
		}

		const valueStore = new StoreValuesConstructor({
			filterQuery: q,
			requestParameters: _requestParameters,
			spec: selectedSpec,
			typeNode: _domainType,
		});

		try {
			// query values
			const possibleItems = await convertValueStoreToComponentFilterItems(valueStore, inMatrix);

			runInAction(() => {
				this._valueStoresBySpec.set(selectedSpec.getKey(), possibleItems);
			});
		} catch (error) {
			console.error(error);
		}
	}

	/** handler for quick filters */
	@action quickFilterOnChange = (quickFilterId: string): void => {
		this._processQuickFilter(quickFilterId);
	};

	/** set deselect ids per spec */
	@action setDeselectIds = (filterSpecId: string, filterValueIds: string[]): void => {
		this._deselectValuesMap.set(filterSpecId, filterValueIds);
	};

	/** set the currently expanded item */
	@action setExpandedItem = (expandedItem: ComponentItem): void => {
		this._expandedItem = expandedItem;
	};

	/** set the reset filter flag */
	@action setFilterReset = (nodeTypeOfAttribute: string, reset: boolean): void => {
		this._resetFilterMap.set(nodeTypeOfAttribute, reset);
	};

	/** check if this spec is part of matrix */
	specIsInMatrix = (spec: Spec): boolean => {
		return !!spec.matrixExtendedPath;
	};

	/** delete the filter and deselect the one in the left filter list as well */
	@action deleteFilter = (item: ComponentItem, filterSpec: Spec, cnlsAndValues: CnlsAndValues): void => {
		const { setFilterSpec, setDeselectIds } = this;

		setFilterSpec(filterSpec, cnlsAndValues, false);
		setDeselectIds(filterSpec.getKey(), [item.id || ""]);
	};

	/** reset the whole attribute filters based on the path  */
	@action resetFilter = (matrixPath?: ExtendedPath): void => {
		const {
			_domainType,
			filterSpecAndValuesForDomainType,
			filterSpecAndValuesForNodeInMatrixPath,
			removeFilters,
			setFilterReset,
		} = this;

		const type = matrixPath ? matrixPath.getEndType() : _domainType;
		const targetList = matrixPath ? filterSpecAndValuesForNodeInMatrixPath : filterSpecAndValuesForDomainType;
		const specs = targetList.map((item) => item.spec);

		setFilterReset(type, true);
		removeFilters(specs);;
	};

	/** reset quick filter */
	@action resetQuickFilter = (): void => {
		this._processQuickFilter(RULE_FILTER_ALL_ITEMS);
	};

	/**
	 * Set filter map
	 * @param filterSpec - the attribute spec
	 * @param cnlsAndValues - the selected value and cnls
	 * @param on - if true indicate value checked, otherwise, unchecked
	 * @param ignoreValue - only makes sense when on = false, if true indicate remove this attribute filter no matter what the values are
	 */
	@action setFilterSpec = (filterSpec: Spec, cnlsAndValues: CnlsAndValues, on: boolean, ignoreValue = false): void => {
		const { _filterValuesMap, _postFilter } = this;

		if (!_filterValuesMap.get(filterSpec)) {
			_filterValuesMap.set(filterSpec, { values: [] });
		}

		let finalValues = _filterValuesMap.get(filterSpec).values;
		let finalCnls = _filterValuesMap.get(filterSpec).cnls || [];
		const { cnls, values } = cnlsAndValues;
		const value = values?.[0];

		if (on) {
			// add value
			_filterValuesMap.set(filterSpec, { cnls: [...finalCnls, ...cnls], values: [...finalValues, value] });
		} else {
			// remove the value
			finalValues = [...finalValues];

			finalValues.splice(finalValues.indexOf(value), 1);
			finalCnls = _.difference(finalCnls, cnls);

			if (finalValues.length == 0 || ignoreValue) {
				_filterValuesMap.delete(filterSpec);
			} else {
				_filterValuesMap.set(filterSpec, { cnls: finalCnls, values: finalValues });
			}
		}

		_postFilter?.(this._filterValuesMap);
	};

	/** mass remove the filters */
	@action removeFilters = (filterSpecs: Spec[]): void => {
		filterSpecs.forEach((spec) => this.setFilterSpec(spec, { values: [] }, false, true));
	};

	@action setFilterQuery = (filterQuery: Query): void => {
		this._filterQuery = filterQuery;
	};

	@action setSpecs = (specs: Spec[]): void => {
		this._specs = specs;
	};

	/** callback when expand the accordion */
	onExpand = (item: ComponentItem): void => {
		const { selectedSpec } = this;

		this.loadValueStoresMap(selectedSpec);
	};

	// callback and re-expand the specs
	@action private _processQuickFilter = (quickFilterId: string): void => {
		const { _postQuickFilter, reloadValuesStore } = this;
		this._quickFilterId = quickFilterId;

		// call back
		_postQuickFilter(quickFilterId);

		// need to re-expanded the specs
		reloadValuesStore();
	};

	/**
	 * @param domainType 
	 * @param specs specs to filter by 
	 * @param requestParameters parameters
	 * @param postFilter callback once any item checked, would carry all specs of items which are being checked
	 */
	constructor(
		domainType: string,
		specs: Spec[],
		filterQuery?: Query,
		requestParameters?: ParameterMap,
		postFilter?: (filterValuesMap: ObservableMap<Spec, CnlsAndValues>) => void,
		quickFilterResources?: string[],
		postQuickFilter?: (quickFilterId: string) => void,
	) {
		makeObservable(this);
		this._domainType = domainType;
		this._filterQuery = filterQuery;
		this._requestParameters = {
			[QRY_VALUE_CNL_LIMIT]: QUERY_RESULT_LIMIT,
			...requestParameters,
		};
		this._specs = specs;
		this._postFilter = postFilter;
		this._quickFilterResources = quickFilterResources;
		this._postQuickFilter = postQuickFilter;
	}
}

export default FilterStore;

export const FilterStoreContext = React.createContext<FilterStore>(undefined);
export const useFilterStore = (): FilterStore => useContext(FilterStoreContext);
